import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import InlineEditField from "components/CustonEditableInlineField/CustomEditableInlineField";
import { formatDate as formatAppDate } from "utils/DateTimeConversion";
import { useTheme } from "@mui/material/styles";
import { DetailField, DetailFieldGrid, UiDetailSection } from "components/v2/record/DetailSection";

export const TravelDetailSection = ({ title, children, mb = 3 }) => (
  <UiDetailSection
    title={title}
    // v2 keeps the caller's spacing (mb={0} in gap layouts); the default 3
    // leaves DetailSection's own rhythm. The Card rule repeats it so a flat
    // section inside a Card honours it too.
    sx={mb === 3 ? undefined : { mb, ".MuiCard-root &": { mb } }}
    legacy={
      <MDBox
        mb={mb}
        p={3}
        borderRadius="lg"
        sx={{
          backgroundColor: "#ffffff",
          boxShadow: 3,
        }}
      >
        {title ? (
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            {title}
          </MDTypography>
        ) : null}
        {children}
      </MDBox>
    }
  >
    {children}
  </UiDetailSection>
);

TravelDetailSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  mb: PropTypes.number,
};

export const formatTravelDateTime = (value) =>
  value ? formatAppDate(value, "datetime") || "-" : "-";

export const formatTravelDate = (value) =>
  value ? formatAppDate(value, "date") || "-" : "-";

/**
 * Audit fields matching LeadDetail / AccountDetail:
 * Created By / Modified By (clickable) + Created / Modified dates.
 */
export const buildTravelAuditFields = (onUserClick) => [
  {
    label: "Created By",
    field: "created_by_name",
    custom: (data) => {
      const name = data?.created_by_name;
      const userId = data?.created_by;
      const canLink = Boolean(userId && name && onUserClick);
      return (
        <InlineEditField
          label="Created By"
          value={name || "-"}
          readOnly
          isEditing={false}
          renderValue={(val) =>
            canLink ? (
              <MDTypography
                component="span"
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{ cursor: "pointer" }}
                onClick={() => onUserClick(userId)}
              >
                {val}
              </MDTypography>
            ) : (
              val || "-"
            )
          }
        />
      );
    },
  },
  {
    label: "Modified By",
    field: "updated_by_name",
    custom: (data) => {
      const name = data?.updated_by_name;
      const userId = data?.updated_by;
      const canLink = Boolean(userId && name && onUserClick);
      return (
        <InlineEditField
          label="Modified By"
          value={name || "-"}
          readOnly
          isEditing={false}
          renderValue={(val) =>
            canLink ? (
              <MDTypography
                component="span"
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{ cursor: "pointer" }}
                onClick={() => onUserClick(userId)}
              >
                {val}
              </MDTypography>
            ) : (
              val || "-"
            )
          }
        />
      );
    },
  },
  {
    label: "Created Date",
    field: "created_at",
    renderValue: formatTravelDateTime,
  },
  {
    label: "Modified Date",
    field: "updated_at",
    renderValue: formatTravelDateTime,
  },
];

const HtmlDetailValue = ({ label, value }) => {
  const html = typeof value === "string" ? value.trim() : "";
  const isEmpty = !html || html === "<p><br></p>" || html === "<p></p>";

  return (
    <MDTypography variant="button" fontWeight="regular" color="text" component="div">
      <strong>{label}:</strong>{" "}
      {isEmpty ? (
        "-"
      ) : (
        <MDBox
          component="span"
          sx={{
            display: "inline",
            "& p": { display: "inline", m: 0 },
            "& p + p": { display: "block", mt: 0.75 },
            "& ul, & ol": { pl: 2.5, my: 0.5 },
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }),
          }}
        />
      )}
    </MDTypography>
  );
};

HtmlDetailValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

const renderV2FieldCell = (data, item) => {
  if (item.custom) return item.custom(data);
  if (item.html) {
    const html = typeof data?.[item.field] === "string" ? data[item.field].trim() : "";
    const isEmpty = !html || html === "<p><br></p>" || html === "<p></p>";
    return (
      <DetailField label={item.label}>
        {isEmpty ? null : (
          <MDBox
            component="div"
            sx={{ "& p": { m: 0 }, "& p + p": { mt: 0.75 }, "& ul, & ol": { pl: 2.5, my: 0.5 } }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }),
            }}
          />
        )}
      </DetailField>
    );
  }
  return (
    <InlineEditField
      label={item.label}
      value={data?.[item.field]}
      readOnly
      isEditing={false}
      renderValue={item.renderValue}
    />
  );
};

// v2: the shared two-column field grid; the cells are the same fields.
const TravelDetailFieldGridV2 = ({ data, fields }) => (
  <DetailFieldGrid>
    {fields.map((item) => (
      <MDBox key={item.field} sx={{ minWidth: 0, ...(item.fullWidth ? { gridColumn: "1 / -1" } : {}) }}>
        {renderV2FieldCell(data, item)}
      </MDBox>
    ))}
  </DetailFieldGrid>
);

TravelDetailFieldGridV2.propTypes = {
  data: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const TravelDetailFieldGrid = ({ data, fields }) =>
  useTheme()?.pearlUi === "v2" ? <TravelDetailFieldGridV2 data={data} fields={fields} /> : (
  <Grid container spacing={3}>
    {fields.map((item) => (
      <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.field}>
        {item.custom ? (
          item.custom(data)
        ) : item.html ? (
          <HtmlDetailValue label={item.label} value={data?.[item.field]} />
        ) : (
          <InlineEditField
            label={item.label}
            value={data?.[item.field]}
            readOnly
            isEditing={false}
            renderValue={item.renderValue}
          />
        )}
      </Grid>
    ))}
  </Grid>
);

TravelDetailFieldGrid.propTypes = {
  data: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const formatPaymentType = (type) => {
  if (type === "full_payment") return "Full Payment";
  if (type === "deposit_final_balance") return "Deposit + Final Balance";
  if (type === "deposit_installments") return "Deposit + Installments";
  return type || "-";
};
